<template>
  <div class="input-timepicker">
    <div class="flex w-full min-w-0 flex-col space-y-2">
      <div class="flex min-w-0 space-x-2">
        <input
          ref="inputElem"
          v-model="textValue"
          v-bind="$attrs"
          :placeholder="$attrs.placeholder ? $attrs.placeholder.toString() : '00:00'"
          :type="inputElemType"
          v-maska
          data-maska="##:##"
          draggable="false"
          class="surface focus:border-secondary focus:ring-secondary disabled:dark:bg-surface/5! field-sizing-content w-full rounded-md border-none px-2 py-1 leading-none focus:ring-2 disabled:cursor-not-allowed! disabled:bg-gray-50! disabled:text-gray-400! sm:text-sm"
          :class="{ 'border-error border-2 border-solid': errors.length > 0 }"
          @click="onClick"
          @focusin="hasFocus = true"
          @focusout="onFocusOut"
          @dragstart.prevent
          @keydown="onKeyDown"
          :pattern="inputElemType === 'text' || inputElemType === 'number' ? '\\d*' : undefined"
          :inputmode="
            inputElemType === 'text' || inputElemType === 'number' ? 'numeric' : undefined
          "
        />
        <!-- Date -->
        <MultiBadge v-if="modelValue && showDate" class="whitespace-nowrap">
          <span v-if="dateLabel">{{ dateLabel }}</span>
          <span v-else-if="date">{{ dtDate }}</span>
          <FontAwesomeIcon v-else icon="arrow-right" />
        </MultiBadge>
      </div>
      <div v-if="!hideErrorMessage && errors.length > 0" class="text-error text-sm select-none">
        {{ errors[0].$message }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, useAttrs, watch } from "vue"
import moment from "moment/moment"
import { type Moment } from "moment"
import type { ErrorObject } from "@vuelidate/core"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import MultiBadge from "@/components/misc/MultiBadge.vue"
import { vMaska } from "maska/vue"
import { createTimeMoment } from "@/helper/time-helper"

const modelValue = defineModel<Moment | null | undefined>({ required: true })

const props = withDefaults(
  defineProps<{
    /**
     * If date is set, the returned Moment will have the same date as this prop. Else, it will have the current date.
     */
    date?: Moment | null
    errors?: ErrorObject[]
    hideErrorMessage?: boolean
    /**
     * If true, the date of the datetime (NOT THE DATE PROP) will be displayed.
     */
    showDate?: boolean
    /**
     * Optional label rendered in the date badge instead of the concrete date. Use this to show a
     * date-agnostic indication (e.g. "volgende dag") when there is no meaningful calendar date,
     * such as in templates. Only applies when `showDate` is true.
     */
    dateLabel?: string | null
    /**
     * If true, the emitted Moment will be tagged as time-only so that
     * DataHelper serializes it as HH:mm:ss for Django TimeField.
     */
    timeOnly?: boolean
  }>(),
  {
    date: null,
    errors: () => [],
    hideErrorMessage: false,
    showDate: false,
    dateLabel: null,
    timeOnly: false
  }
)

const emit = defineEmits(["update:modelValue", "focusout"])

defineOptions({
  inheritAttrs: false
})

const attrs = useAttrs()

const inputElem = ref()

const inputElemType = computed((): string => {
  return attrs.type ? attrs.type.toString() : "text"
})

const textValue = ref("")
if (modelValue.value) {
  textValue.value = modelValue.value.format("HH:mm")
}

const hasFocus = ref(false)

const dtDate = computed(() => {
  return modelValue.value?.format("D-M")
})

function onFocusOut() {
  hasFocus.value = false

  if (inputElemType.value === "text" || inputElemType.value === "number") {
    // Not applicable for native time picker.

    const isInputValid = setTimeIfInputValid()
    if (!isInputValid) {
      // User entered an invalid time string, so let's try to complete it.
      if (parseInt(textValue.value) < 10) {
        // User entered a single number (1-9).
        textValue.value = `0${parseInt(textValue.value)}:00`
      } else {
        // Cannot complete the input, so reset it.
        modelValue.value = null
        textValue.value = ""
      }
    }
  }
  // Wrapping in nextTick ensure that the modelValue has been updated. So parent components will always
  // have the updated value when listening to focusout events.
  nextTick(() => {
    emit("focusout")
  })
}

function onKeyDown(event: KeyboardEvent) {
  const key = event.key

  // Modify time with up/down keys
  if (key === "ArrowUp" || key === "ArrowDown") {
    event.preventDefault()
    const cursorPosition = inputElem.value.selectionStart
    let [hours, minutes] = textValue.value.split(":").map(Number)

    // Only update value when hours and minutes are set
    if (hours && minutes) {
      if (cursorPosition <= 2) {
        // Hours
        if (key === "ArrowUp") {
          hours = (hours + 1) % 24
          if (props.date && hours === 0 && modelValue.value) {
            modelValue.value = modelValue.value.add(1, "day")
          }
        } else {
          hours = (hours - 1 + 24) % 24
          if (props.date && hours === 23 && modelValue.value) {
            modelValue.value = modelValue.value.subtract(1, "day")
          }
        }
        textValue.value = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`
        selectHours()
      } else {
        // Minutes
        if (key === "ArrowUp") {
          minutes = (minutes + 1) % 60
        } else {
          minutes = (minutes - 1 + 60) % 60
        }
        textValue.value = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`
        selectMinutes()
      }
    }

    setTimeIfInputValid()
  }
}

function onClick() {
  if (inputElemType.value !== "text") {
    return // Disable for native time picker.
  }

  if (inputElem.value.selectionStart === inputElem.value.selectionEnd) {
    // User placed cursor on a single position.
    if (inputElem.value.selectionStart <= 2) {
      // Cursor is in hours region.
      selectHours()
    } else {
      // Cursor is in minutes region.
      selectMinutes()
    }
  }
}

function selectMinute() {
  nextTick(() => {
    inputElem.value.selectionStart = 4
    inputElem.value.selectionEnd = 5
  })
}

function selectMinutes() {
  nextTick(() => {
    inputElem.value.selectionStart = 3
    inputElem.value.selectionEnd = 5
  })
}

function selectHours() {
  nextTick(() => {
    inputElem.value.selectionStart = 0
    inputElem.value.selectionEnd = 2
  })
}

function setTimeIfInputValid() {
  const mom = moment(textValue.value, "HH:mm", true)
  if (mom.isValid()) {
    // User has entered a time string conforming HH:mm.
    if (props.date) {
      // Date is set, so we need to create a Moment with the same date as props.date.
      modelValue.value = moment()
        .clone()
        .year(props.date.year())
        .month(props.date.month())
        .date(props.date.date())
        .hours(mom.hours())
        .minutes(mom.minutes())
        .seconds(0)
    } else if (props.timeOnly) {
      // TimeOnly is set, so we need to create a time-only Moment.
      modelValue.value = createTimeMoment(mom).hours(mom.hours()).minutes(mom.minutes()).seconds(0)
    } else {
      // Neither date nor timeOnly is set, so we can use a regular Moment.
      // TODO this branch should not be necessary. It is to support legacy time fields that are DateTimeField but should be TimeField.
      // TODO e.g. Instruction.
      modelValue.value = mom.clone().hours(mom.hours()).minutes(mom.minutes()).seconds(0)
    }
    return true
  }
  return false
}

function autoFill(newVal: string, oldVal: string) {
  if (newVal.length === 2 && oldVal.length === 1) {
    if (newVal.endsWith(":")) {
      // e.g. user types '1:' --> 01: --> 01:00
      textValue.value = `0${newVal[0]}:00`
      selectMinutes()
    } else if (newVal[0] === "2") {
      // User types 2
      if (["0", "1", "2", "3"].includes(newVal[1])) {
        // e.g. user types 22 --> 22:00
        textValue.value = `${newVal}:00`
        selectMinutes()
      } else if (["4", "5"].includes(newVal[1])) {
        // e.g. user types 24 --> 02:40
        textValue.value = `02:${newVal[1]}0`
        selectMinute()
      } else {
        // Invalid --> reset
        textValue.value = ""
      }
    } else if (["3", "4", "5", "6", "7", "8", "9"].includes(newVal[0])) {
      // E.g. user types 6
      if (["0", "1", "2", "3", "4", "5"].includes(newVal[1])) {
        // E.g. user types 5 --> 03:50
        textValue.value = `0${newVal[0]}:${newVal[1]}0`
        selectMinute()
      } else {
        // Invalid --> reset
        textValue.value = ""
      }
    } else {
      // 0 or 1 --> 01, 02, 14, 19 etc. all valid.
      textValue.value = `${newVal}:00`
      selectMinutes()
    }
  } else if (newVal.length === 4 && oldVal.length === 3) {
    // Handle case where user enters a single digit for minutes (e.g., "10:3")
    const [hours, minutes] = newVal.split(":")
    if (hours && minutes && minutes.length === 1) {
      textValue.value = `${hours}:${minutes}0`
      selectMinute()
    }
  }
}

watch(textValue, (newVal, oldVal) => {
  if (inputElemType.value === "text") {
    autoFill(newVal, oldVal) // Autofill only in text mode.
  }
  const isInputValid = setTimeIfInputValid()
  if (!isInputValid) {
    modelValue.value = null
  }
})

function select() {
  inputElem.value.select()
}

function clear() {
  modelValue.value = null
  textValue.value = ""
}

defineExpose({
  select,
  clear
})

watch(modelValue, (val) => {
  if (hasFocus.value) {
    return // Don't interfere with an editing user.
  }

  if (val) {
    textValue.value = val.format("HH:mm")
  } else {
    textValue.value = ""
  }
})

watch(
  () => props.date,
  (val) => {
    // Only realign when the calendar day actually differs. Guarding on the day is essential:
    // callers often pass a freshly cloned Moment (e.g. `date.clone().add(1, 'day')`) on every
    // render, so without this check the reassignment below would re-trigger this watcher
    // indefinitely. It also avoids needless revalidation churn.
    if (modelValue.value && val && !modelValue.value.isSame(val, "day")) {
      // Reassign a fresh clone (new reference) so reactivity/validation re-runs.
      // Copy the full year/month/date to stay correct across month/year boundaries;
      // mutating in place with .date() would neither trigger reactivity nor handle
      // month rollovers (e.g. cross-midnight ranges at the end of a month).
      modelValue.value = modelValue.value
        .clone()
        .year(val.year())
        .month(val.month())
        .date(val.date())
    }
  }
)

watch(
  [() => props.timeOnly, () => props.date],
  ([timeOnly, date]) => {
    if (timeOnly && date) {
      console.warn(
        "InputTimepicker: 'timeOnly' and 'date' props should not be used together. " +
          "'timeOnly' emits a time-only Moment, while 'date' sets the date portion of the Moment."
      )
    }
  },
  { immediate: true }
)
</script>

<style scoped>
input::-webkit-calendar-picker-indicator {
  margin: 0;
}
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type="number"] {
  appearance: textfield;
  -moz-appearance: textfield;
}
</style>
